import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, shareReplay } from 'rxjs';
import {
  CompleteRegistrationGetUser,
  CompleteRegistrationRequest,
  PagedResult,
  PaginationForm,
  SelectData,
  SuccessOutput,
  UserFormRequest,
  UserListItem,
  UserListResponseItem,
  UserProfile,
  UsersGroups,
} from '@shared/models';
import { cleanEmptyProps } from '@shared/functions';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly api = `${environment.baseUrl}/users`;
  private readonly guestUsers = `${environment.baseUrl}/guest-users`;
  private groupsCache$?: Observable<SelectData[]>;

  constructor(private http: HttpClient) {}

  getAll(filters: Partial<PaginationForm>): Observable<SuccessOutput<PagedResult<UserListItem>>> {
    const { filters: search, ...pagination } = filters || {};

    return this.http
      .get<SuccessOutput<PagedResult<UserListResponseItem>>>(`${this.api}`, {
        params: cleanEmptyProps({ ...pagination, search }),
      })
      .pipe(
        map((response) => ({
          ...response,
          data: {
            ...response.data,
            items: response.data.items.map((item) => this.mapUserListItem(item)),
          },
        }))
      );
  }

  private mapUserListItem(item: UserListResponseItem): UserListItem {
    const fullName = `${item.firstName || ''} ${item.lastName || ''}`.trim();

    return {
      id: item.id,
      username: fullName || item.username,
      email: item.email,
      cpf: item.attributes?.documentNumber?.[0] || '',
      groupDescription: item.groupDescription,
      enabled: item.enabled,
    };
  }

  unblock(id: string): Observable<void> {
    return this.http.put<void>(`${this.api}/${id}/unblock`, {});
  }

  register(body: UserFormRequest): Observable<any> {
    return this.http.post<any>(`${this.api}/send-invitation`, body);
  }

  /**
   * Convida um usuário por e-mail. O grupo inicial (padrão, sem permissões) é
   * definido pelo backend — o admin troca depois pela ação de editar grupo.
   */
  invite(email: string): Observable<void> {
    return this.http.post<void>(`${this.api}/send-invitation`, { email });
  }

  /** Reenvia o convite de um usuário que ainda não concluiu o cadastro. */
  resendInvitation(id: string): Observable<void> {
    return this.http.post<void>(`${this.api}/${id}/resend-invitation`, {});
  }

  /** Troca o grupo (perfil de permissões) de um usuário já cadastrado. */
  updateGroup(id: string, groupId: string): Observable<void> {
    return this.http.put<void>(`${this.api}/${id}/group`, { groupId });
  }

  /** Ativa/desativa o usuário — mesma convenção dos demais recursos. */
  updateActiveStatus(id: string, isActive: boolean): Observable<void> {
    return this.http.put<void>(`${this.api}/${id}/active-status`, { isActive });
  }

  getUsersGroups(): Observable<SuccessOutput<UsersGroups[]>> {
    return this.http.get<SuccessOutput<UsersGroups[]>>(`${this.api}/groups`);
  }

  /** Grupos como opções de select, com cache — evita refazer a chamada por diálogo. */
  getGroupsAsSelectOptions(): Observable<SelectData[]> {
    this.groupsCache$ ??= this.getUsersGroups().pipe(
      map((res) => (res.data ?? []).map((group) => ({ label: group.name, value: group.id }))),
      shareReplay(1),
    );

    return this.groupsCache$;
  }

  getUserCompleteRegistration(id: string): Observable<CompleteRegistrationGetUser> {
    return this.http.get<CompleteRegistrationGetUser>(`${this.guestUsers}/${id}`);
  }

  completeRegistration(body: CompleteRegistrationRequest): Observable<void> {
    return this.http.post<void>(`${this.api}`, body);
  }

  getById(id: string): Observable<SuccessOutput<UserProfile>> {
    return this.http.get<SuccessOutput<UserProfile>>(`${this.api}/${id}`);
  }

  getProfileImage(): Observable<SuccessOutput<string>> {
    return this.http.get<SuccessOutput<string>>(`${this.api}/profile-image`);
  }

  uploadProfileImage(file: File): Observable<SuccessOutput<string>> {
    const form = new FormData();
    form.append('File', file);
    return this.http.post<SuccessOutput<string>>(`${this.api}/profile-image`, form);
  }

  updateProfileImage(file: File): Observable<SuccessOutput<string>> {
    const form = new FormData();
    form.append('File', file);
    return this.http.put<SuccessOutput<string>>(`${this.api}/profile-image`, form);
  }

  deleteProfileImage(): Observable<void> {
    return this.http.delete<void>(`${this.api}/profile-image`);
  }
}
